import classNames from 'classnames'
import { createRef, memo, useEffect, useState } from 'react'
// @ts-ignore
import { SelectCallback } from 'react-bootstrap'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { v4 } from 'uuid'

import Icon from '@/components/Icon/Icon'
import Mana from '@/components/Mana/Mana'
import Spinner from '@/components/Spinner/Spinner'
import Typeahead from '@/components/Typeahead/Typeahead'
import { ScryfallTagType } from '@/services/typeahead.service'
import { update } from '@/store/cardlists/actions/update'
import { SetType } from '@/store/misc/initialState'
import { DEFAULT_FILTER_VALUE, DEFAULT_FILTER_VALUE_MAP, FilterType, NAME_MAP } from '@/types/additionalFilters'

import styles from './AdditionalFilters.module.scss'

type RowProps = {
  filter: FilterType
  focusRef?: React.RefObject<HTMLInputElement>
  hideRemove?: boolean
  onChangeFilter: (e: SelectCallback<HTMLInputElement>) => void
  onChangeValues?: (values: FilterType['values']) => void
  onDelete?: () => void
}

export const Row = ({ filter, focusRef, hideRemove, onChangeFilter, onChangeValues, onDelete }: RowProps) => {
  const { key, type, values } = filter

  const typeInput = (
    <div className={styles.typeInput}>
      <Form.Control as='select' onChange={(e) => onChangeFilter(e.target.value)} value={type}>
        {[
          'cardName',
          'typeLine',
          'colors',
          'manaValue',
          'power',
          'toughness',
          'rarity',
          'sets',
          'tags',
          'price',
          'salt',
          'inclusion',
          'num_decks',
          'lift',
          'synergy',
        ].map((value) => (
          <option key={value} value={value}>
            {NAME_MAP[value as keyof typeof NAME_MAP]}
          </option>
        ))}
      </Form.Control>
    </div>
  )

  const deleteButton = (
    <Button className='px-0' onClick={onDelete} variant='link'>
      <Icon icon='times' />
    </Button>
  )

  return (
    <div className={styles.row}>
      {values &&
        (hideRemove || (
          <>
            {['cardName', 'typeLine'].includes(type) ? (
              <div className='d-flex gap-2'>
                {deleteButton}
                {typeInput}
                <Form.Control
                  className={styles.input}
                  onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                  placeholder={NAME_MAP[type as keyof typeof NAME_MAP]}
                  ref={focusRef}
                  required
                  value={values.value}
                />
              </div>
            ) : [
                'inclusion',
                'lift',
                'manaValue',
                'num_decks',
                'power',
                'price',
                'salt',
                'synergy',
                'toughness',
              ].includes(type) ? (
              <div className='d-flex gap-2 w-100'>
                {deleteButton}
                {typeInput}
                <div className={styles.opInput}>
                  <Form.Control
                    as='select'
                    onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                    value={values.operator}
                  >
                    <option value='>'>&gt;</option>
                    <option value='>='>&ge;</option>
                    <option value='==='>=</option>
                    <option value='<'>&lt;</option>
                    <option value='<='>&le;</option>
                    <option value='!=='>&ne;</option>
                  </Form.Control>
                </div>
                <div>
                  <Form.Control
                    className={styles.input}
                    onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                    placeholder={NAME_MAP[type as keyof typeof NAME_MAP]}
                    ref={focusRef}
                    required
                    type='number'
                    value={values.value}
                  />
                </div>
              </div>
            ) : type === 'universesBeyond' ? (
              <div className='align-items-center d-flex gap-2 w-100'>
                {deleteButton}
                <Form.Check
                  checked={values.checked}
                  id={key}
                  label='Hide Universes Beyond exclusive cards'
                  onChange={() => {
                    if (onChangeValues) onChangeValues({ checked: !values.checked })
                  }}
                  type='checkbox'
                />
              </div>
            ) : type === 'colors' ? (
              <>
                {deleteButton}
                {typeInput}
                {['w', 'u', 'b', 'r', 'g', 'c'].map((color) => {
                  const myRef = createRef<HTMLInputElement>()
                  return (
                    <div className={styles.color} key={`${key}-${color}`}>
                      <Form.Check
                        checked={!!values[color as keyof FilterType['values']]}
                        id={`${key}-${color}`}
                        label={<Mana className={styles.mana} color={color as 'w' | 'u' | 'b' | 'r' | 'g' | 'c'} />}
                        onChange={() => {
                          if (onChangeValues) onChangeValues({ [color]: !values[color as keyof FilterType['values']] })
                          if (myRef.current) myRef.current.blur()
                        }}
                        ref={myRef}
                        type='checkbox'
                      />
                    </div>
                  )
                })}
                <div>
                  <Form.Control
                    as='select'
                    onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                    value={values.operator}
                  >
                    <option value='EXCLUSIVE ALL'>Exactly these colors</option>
                    <option value='INCLUSIVE ALL'>Including these colors</option>
                    <option value='SOME'>At most these colors</option>
                  </Form.Control>
                </div>
              </>
            ) : type === 'rarity' ? (
              <div className='d-flex gap-2 w-100'>
                {deleteButton}
                {typeInput}
                <div>
                  <Form.Control
                    as='select'
                    onChange={(e) => onChangeValues && onChangeValues({ value: e.target.value })}
                    value={values.value}
                  >
                    <option value='common'>Common</option>
                    <option value='uncommon'>Uncommon</option>
                    <option value='rare'>Rare</option>
                    <option value='mythic'>Mythic</option>
                  </Form.Control>
                </div>
              </div>
            ) : type === 'sets' ? (
              <>
                {deleteButton}
                {typeInput}
                <Form.Control
                  as='select'
                  className='w-auto'
                  onChange={(e) => onChangeValues && onChangeValues({ operator: e.target.value })}
                  value={values.operator}
                >
                  <option value='INCLUDE'>Include</option>
                  <option value='EXCLUDE'>Exclude</option>
                </Form.Control>
                <Typeahead
                  onChange={(sets) =>
                    onChangeValues &&
                    onChangeValues({
                      sets: (sets as SetType[]).map(({ code }) => code),
                    })
                  }
                  placeholder='Sets'
                  type='sets'
                />
              </>
            ) : type === 'tags' ? (
              <div className='d-flex gap-2 w-100'>
                {deleteButton}
                {typeInput}
                <Typeahead
                  className={styles.input}
                  onChange={(tags) =>
                    onChangeValues &&
                    onChangeValues({
                      value: (tags as ScryfallTagType[]).map(({ id }) => id),
                    })
                  }
                  placeholder='Tags'
                  type='tags'
                />
              </div>
            ) : null}
          </>
        ))}
    </div>
  )
}

type Props = {
  className?: string
  filters: FilterType[]
  onSubmitFinish?: () => void
  setFilters: (filters: FilterType[]) => void
}

const AdditionalFilters = ({ className, filters, onSubmitFinish, setFilters }: Props) => {
  const [isLoading, setIsLoading] = useState(false)

  const resetFilters = () => {
    setFilters([])
    update({ filters: [] })
  }

  const changeFilter = (filter: keyof typeof DEFAULT_FILTER_VALUE_MAP, i: number) => {
    const newValues = DEFAULT_FILTER_VALUE_MAP[filter] || DEFAULT_FILTER_VALUE
    setFilters([
      ...filters.slice(0, i),
      {
        focus: true,
        key: v4(),
        type: filter,
        values: newValues,
      },
      ...filters.slice(i + 1),
    ])
  }

  const changeValues = (values: FilterType['values'], i: number) => {
    if (!values) return
    let newValues = values
    if (filters[i].type === 'colors' && values.c !== undefined) {
      newValues = { ...newValues, ...DEFAULT_FILTER_VALUE_MAP.colors, c: true }
      if (filters[i].values?.operator) newValues.operator = filters[i].values?.operator
    } else if (filters[i].type === 'colors' && ['w', 'u', 'b', 'r', 'g'].includes(Object.keys(values)[0])) {
      // Colors handler
      newValues = {
        ...values,
        c: false,
      }
      const { ...colors } = {
        ...filters[i].values,
        ...newValues,
      }
      // Checks colorless if nothing is checked
      if (!Object.values(colors).some((c) => c)) {
        newValues.c = true
      }
    }
    setFilters([
      ...filters.slice(0, i),
      {
        ...filters[i],
        values: { ...filters[i].values, ...newValues },
      },
      ...filters.slice(i + 1),
    ])
  }

  const deleteFilter = (i: number) => {
    setFilters([...filters.slice(0, i), ...filters.slice(i + 1)])
  }

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault()
    setIsLoading(true)
    await update({ filters })
    setIsLoading(false)
    if (onSubmitFinish) onSubmitFinish()
  }

  // If a filter row is marked "focus", focus on it
  const focusRef = createRef<HTMLInputElement>()
  useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus()
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setFilters(filters.map(({ focus, ...filter }) => filter))
    }
  }, [focusRef])

  if (filters.length === 0) return null

  const requiredInputIsEmpty = filters.some(
    ({ type, values }) => !['colors', 'universesBeyond'].includes(type) && !values?.value,
  )

  return (
    <form className={classNames('d-flex flex-column gap-2 w-100', className)} onSubmit={handleSubmit}>
      <div className={styles.title}>Card Filters</div>
      {filters.map((filter, i) => (
        <Row
          filter={filter}
          focusRef={filter.focus ? focusRef : undefined}
          key={filter.key}
          onChangeFilter={(e) => changeFilter(e as keyof typeof DEFAULT_FILTER_VALUE_MAP, i)}
          onChangeValues={(values) => changeValues(values, i)}
          onDelete={() => deleteFilter(i)}
        />
      ))}
      {filters.length > 0 && (
        <div>
          <Button disabled={isLoading} type='submit' variant={requiredInputIsEmpty ? 'secondary' : 'primary'}>
            {isLoading ? <Spinner /> : 'Submit'}
          </Button>
          <Button disabled={isLoading} onClick={resetFilters} variant='link'>
            reset
          </Button>
        </div>
      )}
    </form>
  )
}

export default memo(AdditionalFilters)
